import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  ActivityIndicator,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput as RNTextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Card, Divider } from 'react-native-paper';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { ShowToast } from '../Common/common';
import BTButton from '../Components/BTButton';
import BTCheckbox from '../Components/BTCheckbox';
import BTComboBox from '../Components/BTComboBox';
import { Col, Row } from '../Components/BTGrid';
import { TextInput } from '../Components/BTTextInput';
import { useGetEmployeesQuery } from '../redux/services/emmemployeeApi';
import { useInsertFOEHouseKeepingMutation } from '../redux/services/foehousekeepingApi';
import { useGetFORoomssQuery } from '../redux/services/foroomsApi';
import { useGetPublicPlacesQuery } from '../redux/services/hkpublicplaceApi';
import { useInsertPublicCleaningMutation } from '../redux/services/hkpubliccleaningApi';
import { launchCamera, launchImageLibrary } from 'react-native-image-picker';
import { useUploadImageMutation } from '../redux/services/acompanyApi';
import { resolveImageUrl } from '../Common/common';

import { RootState } from '../redux/store';
import styles from '../styles/FOStyles';

import { usePhoneticText } from '../utils/transliterate/usePhoneticInput';
import T from '../Components/T';
import { tp } from '../utils/translateText';
export const ROOM_CHECKLIST_SUGGESTIONS = [
  'Bed Made',
  'Fresh Linen Changed',
  'Bathroom Cleaned & Sanitized',
  'Fresh Towels & Bathmat Restocked',
  'Toiletries & Amenities Restocked',
  'Floor Swept & Mopped',
  'Carpet & Rugs Vacuumed',
  'Dusting Furniture & Fixtures',
  'Trash & Waste Bins Emptied',
  'Mirrors & Glass Surfaces Wiped',
  'AC, TV & Lights Checked & Tested',
  'Drinking Water & Glasses Restocked',
  'Room Freshener Sprayed',
];

export const PUBLIC_CHECKLIST_SUGGESTIONS = [
  'Swept & Mopped Floor',
  'Sanitized High-Touch Surfaces',
  'Emptied Trash & Waste Bins',
  'Restocked Soap & Paper Towels',
  'Disinfected Toilets & Urinals',
  'Wiped Glass & Mirrors',
  'Dusted Furniture & Fixtures',
  'Vacuumed Carpets & Rugs',
  'Cleaned Entrance & Glass Doors',
  'Sprayed Room Freshener',
];

const validationSchema = yup.object({
  hk_room: yup.number().nullable().transform((val) => (isNaN(val) ? null : val)),
  hk_place: yup.number().nullable().transform((val) => (isNaN(val) ? null : val)),
  hk_statusto: yup.string().required('Status is required'),
  hk_employee: yup
    .number()
    .min(1, 'Employee is required')
    .typeError('Employee is required')
    .required('Employee is required'),
  hk_narr: yup.string().nullable().default(''),
});

const HouseKeeping = ({ navigation, route }: UniversalProps) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const cid = gdata.company;

  const initialMode =
    route?.params?.mode === 'PUBLIC_PLACE' || route?.params?.placeId
      ? 'PUBLIC_PLACE'
      : 'ROOM';
  const [cleaningType, setCleaningType] = useState<'ROOM' | 'PUBLIC_PLACE'>(initialMode);

  const [isSaving, setIsSaving] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [photoUri, setPhotoUri] = useState<string | null>(null);
  const [photoKey, setPhotoKey] = useState<string>('');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [uploadImage] = useUploadImageMutation();

  const handleTakePhoto = async () => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.setAttribute('capture', 'environment');
      input.onchange = async (e: any) => {
        const file = e.target.files?.[0];
        if (file) {
          uploadWebFile(file);
        }
      };
      input.click();
      return;
    }

    try {
      launchCamera(
        {
          mediaType: 'photo',
          cameraType: 'back',
          quality: 0.8,
          saveToPhotos: false,
        },
        async (response) => {
          if (response.didCancel) return;
          if (response.errorCode) {
            ShowToast(response.errorMessage || 'Camera error', 'error');
            return;
          }
          const asset = response.assets?.[0];
          if (asset && asset.uri) {
            await uploadNativeAsset(asset);
          }
        }
      );
    } catch (err: any) {
      console.warn('Camera launch error:', err);
      ShowToast('Could not open camera', 'error');
    }
  };

  const handlePickFromGallery = async () => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async (e: any) => {
        const file = e.target.files?.[0];
        if (file) {
          uploadWebFile(file);
        }
      };
      input.click();
      return;
    }

    try {
      launchImageLibrary(
        {
          mediaType: 'photo',
          quality: 0.8,
        },
        async (response) => {
          if (response.didCancel) return;
          if (response.errorCode) {
            ShowToast(response.errorMessage || 'Gallery error', 'error');
            return;
          }
          const asset = response.assets?.[0];
          if (asset && asset.uri) {
            await uploadNativeAsset(asset);
          }
        }
      );
    } catch (err: any) {
      console.warn('Gallery pick error:', err);
      ShowToast('Could not open gallery', 'error');
    }
  };

  const uploadNativeAsset = async (asset: any) => {
    setIsUploadingPhoto(true);
    setPhotoUri(asset.uri);
    try {
      const formData = new FormData();
      formData.append('file', {
        uri: asset.uri,
        type: asset.type || 'image/jpeg',
        name: asset.fileName || `hk_${Date.now()}.jpg`,
      } as any);
      formData.append('folder', 'hk-photos');
      formData.append('fileName', `hk_${Date.now()}`);

      const res = await uploadImage(formData).unwrap();
      if (res?.key) {
        setPhotoKey(res.key);
        ShowToast('Photo uploaded successfully');
      } else {
        throw new Error('No key returned');
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      ShowToast(err?.data?.message || err?.message || 'Failed to upload photo', 'error');
      setPhotoUri(null);
      setPhotoKey('');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const uploadWebFile = async (file: File) => {
    setIsUploadingPhoto(true);
    setPhotoUri(URL.createObjectURL(file));
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'hk-photos');
      formData.append('fileName', `hk_${Date.now()}`);

      const res = await uploadImage(formData).unwrap();
      if (res?.key) {
        setPhotoKey(res.key);
        ShowToast('Photo uploaded successfully');
      } else {
        throw new Error('No key returned');
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      ShowToast(err?.data?.message || err?.message || 'Failed to upload photo', 'error');
      setPhotoUri(null);
      setPhotoKey('');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoUri(null);
    setPhotoKey('');
  };

  const ph = usePhoneticText();
  const [customTask, setCustomTask] = useState('');

  const [insertFOEHouseKeeping] = useInsertFOEHouseKeepingMutation();
  const [insertPublicCleaning] = useInsertPublicCleaningMutation();

  const { data: roomData } = useGetFORoomssQuery(cid);
  const { data: publicPlacesData } = useGetPublicPlacesQuery(cid);
  const { data: employeeData } = useGetEmployeesQuery(cid);

  const rooms = roomData?.map((value) => ({
    label: value.rm_name,
    id: value.rm_id,
    value,
  })) || [];

  const publicPlaces = publicPlacesData?.map((value) => ({
    label: `${value.pl_name} (${value.pl_floor || value.pl_type})`,
    id: value.pl_id,
    value,
  })) || [];

  const employees = employeeData?.map((value) => ({
    label: value.em_name,
    id: value.em_id,
  })) || [];

  const roomStatusList = [
    { label: 'Vacant', id: 'V' },
    { label: 'Occupied', id: 'O' },
    { label: 'Dirty', id: 'D' },
    { label: 'Block', id: 'B' },
    { label: 'Maintenance', id: 'M' },
  ];

  const publicStatusList = [
    { label: 'Clean', id: 'C' },
    { label: 'Dirty / Needs Cleaning', id: 'D' },
    { label: 'Cleaning In Progress', id: 'I' },
    { label: 'Under Maintenance', id: 'M' },
  ];

  const currentSuggestions =
    cleaningType === 'ROOM'
      ? ROOM_CHECKLIST_SUGGESTIONS
      : PUBLIC_CHECKLIST_SUGGESTIONS;

  const currentStatusOptions =
    cleaningType === 'ROOM' ? roomStatusList : publicStatusList;

  const initialValues = {
    hk_id: 0,
    hk_company: cid,
    hk_date: moment(gdata.today).format('YYYY/MM/DD HH:mm:ss'),
    hk_room: 0,
    hk_place: 0,
    hk_statusfrom: '',
    hk_statusto: '',
    hk_employee: 0,
    hk_narr: '',
    hk_flag: '1',
  };

  const methods = useForm<any>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const handleToggleTask = (task: string) => {
    setSelectedTasks((prev) =>
      prev.includes(task) ? prev.filter((t) => t !== task) : [...prev, task]
    );
  };

  const handleSelectAllTasks = () => {
    setSelectedTasks((prev) => {
      const set = new Set([...prev, ...currentSuggestions]);
      return Array.from(set);
    });
  };

  const handleClearAllTasks = () => {
    setSelectedTasks([]);
  };

  const handleAddCustomTask = () => {
    const trimmed = customTask.trim();
    if (!trimmed) return;
    if (!selectedTasks.includes(trimmed)) {
      setSelectedTasks((prev) => [...prev, trimmed]);
    }
    setCustomTask('');
  };

  const handleModeChange = (mode: 'ROOM' | 'PUBLIC_PLACE') => {
    setCleaningType(mode);
    setSelectedTasks([]);
    setCustomTask('');
    methods.setValue('hk_room', 0);
    methods.setValue('hk_place', 0);
    methods.setValue('hk_statusfrom', '');
    methods.setValue('hk_statusto', mode === 'PUBLIC_PLACE' ? 'C' : 'V');
  };

  const onRoomSelect = (roomId: any) => {
    const found = roomData?.find((r) => r.rm_id === Number(roomId));
    if (found) {
      methods.setValue('hk_statusfrom', found.rm_status);
      methods.setValue(
        'hk_statusto',
        found.rm_status === 'O'
          ? 'O'
          : found.rm_status === 'V'
          ? 'B'
          : 'V'
      );
    }
  };

  const onPlaceSelect = (placeId: any) => {
    const found = publicPlacesData?.find((p) => p.pl_id === Number(placeId));
    if (found) {
      methods.setValue('hk_statusfrom', found.pl_status || 'D');
      methods.setValue('hk_statusto', 'C');
      if (found.pl_checklist) {
        try {
          const parsed = JSON.parse(found.pl_checklist);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setSelectedTasks(parsed);
          }
        } catch {
          // ignore parsing error
        }
      }
    }
  };

  const onSubmit: SubmitHandler<any> = async (values) => {
    if (isSaving) return;

    if (cleaningType === 'PUBLIC_PLACE') {
      if (!values.hk_place || Number(values.hk_place) <= 0) {
        ShowToast('Please select a public area / place', 'error');
        return;
      }

      setIsSaving(true);
      try {
        await insertPublicCleaning({
          cl_company: cid,
          cl_date: moment().format('YYYY-MM-DD HH:mm:ss'),
          cl_place: Number(values.hk_place),
          cl_employee: Number(values.hk_employee),
          cl_statusfrom: values.hk_statusfrom || 'D',
          cl_statusto: values.hk_statusto || 'C',
          cl_checklist: JSON.stringify(selectedTasks),
          cl_narr: photoKey ? `${values.hk_narr ? values.hk_narr + ' | ' : ''}Photo: ${photoKey}` : (values.hk_narr || ''),
          cl_uid: gdata.user,
        }).unwrap();

        ShowToast('Public Area Cleaning Saved Successfully');
        if (navigation.canGoBack()) {
          navigation.goBack();
        } else if (gdata.menu?.some((v: any) => v.mn_link === '/status/status')) {
          navigation.navigate('StatusScreen');
        } else {
          navigation.navigate('Dashboard');
        }
      } catch (error: any) {
        ShowToast(
          error?.data?.message || error?.message || 'Failed to save public cleaning',
          'error'
        );
      }
      setIsSaving(false);
      return;
    }

    // Default: ROOM
    if (!values.hk_room || Number(values.hk_room) <= 0) {
      ShowToast('Please select a room', 'error');
      return;
    }

    setIsSaving(true);
    try {
      await insertFOEHouseKeeping({
        ...values,
        hk_room: Number(values.hk_room),
        hk_employee: Number(values.hk_employee),
        hk_photo: photoKey || null,
        hk_narr:
          selectedTasks.length > 0
            ? `${values.hk_narr ? values.hk_narr + ' | ' : ''}Tasks: ${selectedTasks.join(', ')}`
            : values.hk_narr,
      }).unwrap();

      ShowToast('Room Housekeeping Saved Successfully');
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else if (gdata.menu?.some((v: any) => v.mn_link === '/status/status')) {
        navigation.navigate('StatusScreen');
      } else {
        navigation.navigate('Dashboard');
      }
    } catch (error: any) {
      ShowToast(
        error?.data?.message || error?.message || 'Failed to save room housekeeping',
        'error'
      );
    }
    setIsSaving(false);
  };

  useEffect(() => {
    if (route.params?.room) {
      setCleaningType('ROOM');
      const selectedRoom = roomData?.find(
        (v) => v.rm_id === Number(route.params.room)
      );
      methods.reset({
        ...initialValues,
        hk_room: Number(route.params.room),
        hk_statusfrom: selectedRoom ? selectedRoom.rm_status : '',
        hk_statusto: selectedRoom
          ? selectedRoom.rm_status === 'O'
            ? 'O'
            : selectedRoom.rm_status === 'V'
            ? 'B'
            : 'V'
          : '',
      });
    } else if (route.params?.placeId) {
      setCleaningType('PUBLIC_PLACE');
      const selectedPlace = publicPlacesData?.find(
        (p) => p.pl_id === Number(route.params.placeId)
      );
      methods.reset({
        ...initialValues,
        hk_place: Number(route.params.placeId),
        hk_statusfrom: selectedPlace ? selectedPlace.pl_status : 'D',
        hk_statusto: 'C',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.params?.room, route.params?.placeId, roomData, publicPlacesData]);

  return (
    <View style={{ flex: 1, backgroundColor: '#f5f5f5' }}>
      <ScrollView
        contentContainerStyle={{
          padding: 8,
          paddingBottom: 80,
          flexGrow: 1,
          backgroundColor: '#f5f5f5',
        }}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.header}>
          <Text style={styles.title}><T>Housekeeping & Cleaning</T></Text>
        </View>

        {/* Segmented Mode Selector: Guest Rooms vs Public Places */}
        <View style={localStyles.toggleContainer}>
          <TouchableOpacity
            style={[
              localStyles.toggleButton,
              cleaningType === 'ROOM' && localStyles.toggleButtonActive,
            ]}
            onPress={() => handleModeChange('ROOM')}
            activeOpacity={0.8}
          >
            <FontAwesome
              name="bed"
              size={16}
              color={cleaningType === 'ROOM' ? '#ffffff' : '#4b5563'}
              style={{ marginRight: 6 }}
            />
            <Text
              style={[
                localStyles.toggleText,
                cleaningType === 'ROOM' && localStyles.toggleTextActive,
              ]}
            >
              <T>Guest Rooms</T>
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              localStyles.toggleButton,
              cleaningType === 'PUBLIC_PLACE' && localStyles.toggleButtonActive,
            ]}
            onPress={() => handleModeChange('PUBLIC_PLACE')}
            activeOpacity={0.8}
          >
            <FontAwesome
              name="building"
              size={16}
              color={cleaningType === 'PUBLIC_PLACE' ? '#ffffff' : '#4b5563'}
              style={{ marginRight: 6 }}
            />
            <Text
              style={[
                localStyles.toggleText,
                cleaningType === 'PUBLIC_PLACE' && localStyles.toggleTextActive,
              ]}
            >
              <T>Public Areas</T>
            </Text>
          </TouchableOpacity>
        </View>

        <FormProvider {...methods}>
          <Card style={styles.cardfill}>
            <Card.Content>
              {/* Location Selector */}
              {cleaningType === 'ROOM' ? (
                <Row>
                  <Col>
                    <BTComboBox
                      name="hk_room"
                      label="Select Room *"
                      data={rooms}
                      placeholder={tp("Select Room")}
                      onValChange={onRoomSelect}
                    />
                  </Col>
                </Row>
              ) : (
                <Row>
                  <Col>
                    <BTComboBox
                      name="hk_place"
                      label="Select Public Area / Place *"
                      data={publicPlaces}
                      placeholder={tp("Select Area (Lobby, Restroom, etc.)")}
                      onValChange={onPlaceSelect}
                    />
                  </Col>
                </Row>
              )}

              {/* Status Selector */}
              <Row>
                <Col>
                  <BTComboBox
                    name="hk_statusto"
                    label={
                      cleaningType === 'ROOM'
                        ? 'New Room Status *'
                        : 'New Area Status *'
                    }
                    data={currentStatusOptions}
                    placeholder={tp("Select Status")}
                  />
                </Col>
              </Row>

              {/* Attendant / Employee */}
              <Row>
                <Col>
                  <BTComboBox
                    name="hk_employee"
                    label="Housekeeping Attendant *"
                    data={employees}
                    placeholder={tp("Select Attendant")}
                  />
                </Col>
              </Row>

              {/* Checklist Section */}
              <View style={localStyles.checklistSection}>
                <View style={localStyles.checklistHeader}>
                  <Text style={localStyles.checklistTitle}>
                    <FontAwesome name="check-square-o" size={15} color="#146C94" />
                    {' '}<T>Checklist Tasks (</T>{selectedTasks.length})
                  </Text>
                  <View style={{ flexDirection: 'row', gap: 6 }}>
                    <TouchableOpacity
                      style={localStyles.actionButton}
                      onPress={handleSelectAllTasks}
                    >
                      <Text style={localStyles.actionButtonText}><T>Select All</T></Text>
                    </TouchableOpacity>
                    {selectedTasks.length > 0 && (
                      <TouchableOpacity
                        style={localStyles.actionButton}
                        onPress={handleClearAllTasks}
                      >
                        <Text style={[localStyles.actionButtonText, { color: '#dc2626' }]}>
                          <T>Clear</T>
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>
                </View>

                {/* Selected Task Chips */}
                {selectedTasks.length > 0 && (
                  <View style={localStyles.chipContainer}>
                    {selectedTasks.map((task) => (
                      <TouchableOpacity
                        key={task}
                        style={localStyles.activeChip}
                        onPress={() => handleToggleTask(task)}
                      >
                        <FontAwesome name="check" size={11} color="#ffffff" style={{ marginRight: 4 }} />
                        <Text style={localStyles.activeChipText}>{task}</Text>
                        <FontAwesome name="times" size={11} color="#ffffff" style={{ marginLeft: 6 }} />
                      </TouchableOpacity>
                    ))}
                  </View>
                )}

                {/* Quick Add Suggestion Chips */}
                <Text style={localStyles.suggestionHeader}>
                  <T>Tap to add tasks:</T>
                </Text>
                <View style={localStyles.chipContainer}>
                  {currentSuggestions
                    .filter((s) => !selectedTasks.includes(s))
                    .map((s) => (
                      <TouchableOpacity
                        key={s}
                        style={localStyles.suggestionChip}
                        onPress={() => handleToggleTask(s)}
                      >
                        <FontAwesome name="plus" size={10} color="#146C94" style={{ marginRight: 4 }} />
                        <Text style={localStyles.suggestionChipText}>{s}</Text>
                      </TouchableOpacity>
                    ))}
                </View>

                {/* Custom Task Entry */}
                <View style={localStyles.customTaskRow}>
                  <RNTextInput
                    style={localStyles.customTaskInput}
                    placeholder={tp("Add custom task...")}
                    value={customTask}
                    onChangeText={(t) => setCustomTask(ph.onChange(t))}
                    onBlur={() => setCustomTask(ph.onBlur(customTask))}
                    onSubmitEditing={handleAddCustomTask}
                  />
                  <TouchableOpacity
                    style={localStyles.addCustomButton}
                    onPress={handleAddCustomTask}
                  >
                    <Text style={localStyles.addCustomButtonText}><T>Add</T></Text>
                  </TouchableOpacity>
                </View>
              </View>

              {/* Remarks */}
              <Row>
                <Col>
                  <TextInput
                    name="hk_narr"
                    label="Remarks"
                    placeholder={tp("Additional notes...")}
                  />
                </Col>
              </Row>


              {/* Photo Capture Section */}
              <View style={localStyles.photoSection}>
                <View style={localStyles.photoSectionHeader}>
                  <Text style={localStyles.photoSectionTitle}>
                    <FontAwesome name="camera" size={14} color="#146C94" />
                    {' '}<T>Room / Area Photo (Optional)</T>
                  </Text>
                  {photoKey ? (
                    <View style={localStyles.uploadedBadge}>
                      <FontAwesome name="check-circle" size={12} color="#16a34a" />
                      <Text style={localStyles.uploadedText}> <T>Uploaded</T></Text>
                    </View>
                  ) : null}
                </View>

                {photoUri ? (
                  <View style={localStyles.photoPreviewContainer}>
                    <Image
                      source={{ uri: photoUri }}
                      style={localStyles.photoPreviewImage}
                      resizeMode="cover"
                    />
                    {isUploadingPhoto ? (
                      <View style={localStyles.photoUploadingOverlay}>
                        <ActivityIndicator size="small" color="#ffffff" />
                        <Text style={localStyles.uploadingText}><T>Uploading photo...</T></Text>
                      </View>
                    ) : (
                      <TouchableOpacity
                        style={localStyles.removePhotoButton}
                        onPress={handleRemovePhoto}
                        activeOpacity={0.8}
                      >
                        <FontAwesome name="trash" size={14} color="#dc2626" />
                        <Text style={localStyles.removePhotoText}><T>Remove Photo</T></Text>
                      </TouchableOpacity>
                    )}
                  </View>
                ) : (
                  <View style={localStyles.photoButtonsRow}>
                    <TouchableOpacity
                      style={localStyles.cameraButton}
                      onPress={handleTakePhoto}
                      disabled={isUploadingPhoto}
                      activeOpacity={0.8}
                    >
                      <FontAwesome name="camera" size={16} color="#146C94" style={{ marginRight: 6 }} />
                      <Text style={localStyles.cameraButtonText}><T>Take Photo</T></Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={localStyles.galleryButton}
                      onPress={handlePickFromGallery}
                      disabled={isUploadingPhoto}
                      activeOpacity={0.8}
                    >
                      <FontAwesome name="image" size={16} color="#475569" style={{ marginRight: 6 }} />
                      <Text style={localStyles.galleryButtonText}><T>Gallery</T></Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              {/* Active Flag */}
              <Row>
                <Col>
                  <BTCheckbox name="hk_flag" label="Active" />
                </Col>
              </Row>

              <Divider style={{ marginVertical: 12 }} />

              {/* Action Buttons */}
              <Row>
                <Col>
                  <BTButton
                    title={isSaving ? 'Saving...' : 'Save Cleaning'}
                    color="#146C94"
                    onPress={methods.handleSubmit(onSubmit)}
                    onLongPress={() => {}}
                    disabled={isSaving}
                  />
                </Col>
                <Col>
                  <BTButton
                    title="Cancel"
                    color="#64748b"
                    onPress={() => {
                      if (navigation.canGoBack()) {
                        navigation.goBack();
                      } else if (gdata.menu?.some((v: any) => v.mn_link === '/status/status')) {
                        navigation.navigate('StatusScreen');
                      } else {
                        navigation.navigate('Dashboard');
                      }
                    }}
                    onLongPress={() => {}}
                  />
                </Col>
              </Row>
            </Card.Content>
          </Card>
        </FormProvider>
      </ScrollView>
    </View>
  );
};

const localStyles = StyleSheet.create({
  photoSection: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    padding: 12,
    marginVertical: 10,
  },
  photoSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  photoSectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#1e293b',
  },
  uploadedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#dcfce7',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  uploadedText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#16a34a',
  },
  photoPreviewContainer: {
    alignItems: 'center',
    marginVertical: 6,
    position: 'relative',
  },
  photoPreviewImage: {
    width: '100%',
    height: 180,
    borderRadius: 8,
    backgroundColor: '#e2e8f0',
  },
  photoUploadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadingText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    marginTop: 6,
  },
  removePhotoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fee2e2',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    marginTop: 8,
  },
  removePhotoText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#dc2626',
    marginLeft: 6,
  },
  photoButtonsRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 4,
  },
  cameraButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f9ff',
    borderWidth: 1.5,
    borderColor: '#146C94',
    borderStyle: 'dashed',
    paddingVertical: 12,
    borderRadius: 8,
  },
  cameraButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#146C94',
  },
  galleryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#cbd5e1',
    paddingVertical: 12,
    borderRadius: 8,
  },
  galleryButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#475569',
  },
  toggleContainer: {
    flexDirection: 'row',
    backgroundColor: '#e2e8f0',
    borderRadius: 8,
    padding: 3,
    marginBottom: 12,
  },
  toggleButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 6,
  },
  toggleButtonActive: {
    backgroundColor: '#146C94',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#475569',
  },
  toggleTextActive: {
    color: '#ffffff',
  },
  checklistSection: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    padding: 10,
    marginVertical: 10,
  },
  checklistHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  checklistTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#1e293b',
  },
  actionButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#f1f5f9',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#cbd5e1',
  },
  actionButtonText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#146C94',
  },
  suggestionHeader: {
    fontSize: 11,
    fontWeight: '600',
    color: '#64748b',
    marginTop: 6,
    marginBottom: 4,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 6,
  },
  activeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#16a34a',
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 14,
  },
  activeChipText: {
    fontSize: 11,
    color: '#ffffff',
    fontWeight: '600',
  },
  suggestionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderColor: '#cbd5e1',
    borderWidth: 1,
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 14,
  },
  suggestionChipText: {
    fontSize: 11,
    color: '#334155',
    fontWeight: '500',
  },
  customTaskRow: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 8,
  },
  customTaskInput: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    fontSize: 12,
    color: '#1e293b',
  },
  addCustomButton: {
    backgroundColor: '#146C94',
    paddingHorizontal: 14,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 6,
  },
  addCustomButtonText: {
    color: '#ffffff',
    fontWeight: '600',
    fontSize: 12,
  },
});

export default HouseKeeping;
